import { Tooltip } from '@chakra-ui/react'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2NoFieldsSwitch from '@core/components/ApplyDialogV2/ApplyV2NoFieldsSwitch'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import clsx from 'clsx'
import React, { useState, useEffect, useRef } from 'react'
import './ApplyV2EducationStep.scss'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import {
    usePostEducationV2ApplyMutation,
    useDeleteEducationV2ApplyMutation,
    usePutEducationV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'
import { EducationIcon } from './icons/icon'
import { useTranslation } from 'react-i18next'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import ApplyV2Card from '@core/components/ApplyDialogV2/ApplyV2Card'

const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
const monthOptions = months.map((m) => ({ value: m, label: m }))

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 70 }, (_, i) => (currentYear - i).toString())
const endYears = Array.from({ length: 70 }, (_, i) => (currentYear + 6 - i).toString())
const startYearOptions = years.map((y) => ({ value: y, label: y }))
const endYearOptions = endYears.map((y) => ({ value: y, label: y }))

type Education = {
    uid?: string
    institution: string
    degree: string
    educationType: string
    startMonth: string
    startYear: string
    endMonth: string
    endYear: string
    location: string
    description: string
}

const defaultEducation: Education = {
    institution: '',
    degree: '',
    educationType: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    location: '',
    description: '',
}

const mapApiEducation = (apiEdu: any): Education => ({
    uid: apiEdu.uid || '',
    institution: apiEdu.schoolName || '',
    degree: apiEdu.degree || '',
    educationType: apiEdu.educationType || '',
    startMonth: apiEdu.fromDate ? apiEdu.fromDate.split('-')[1] : '',
    startYear: apiEdu.fromDate ? apiEdu.fromDate.split('-')[0] : '',
    endMonth: apiEdu.toDate ? apiEdu.toDate.split('-')[1] : '',
    endYear: apiEdu.toDate ? apiEdu.toDate.split('-')[0] : '',
    location: apiEdu.location || '',
    description: apiEdu.description || '',
})

const baseClass = 'ApplyV2EducationStep'

const EducationStep: React.FC = () => {
    const { t } = useTranslation()
    const { getTitle } = useApplyV2NavTranslations()
    const { postProfileData, onNextStep, postProfileRefetch, isPostProfileLoading, isPostApplied } = useApplyV2Context()

    const [postEducation, { isLoading: isPostLoading }] = usePostEducationV2ApplyMutation()
    const [putEducation, { isLoading: isPutLoading }] = usePutEducationV2ApplyMutation()
    const [deleteEducation, { isLoading: isDeleteLoading }] = useDeleteEducationV2ApplyMutation()

    const educations: Education[] = (postProfileData?.data?.education ?? postProfileData?.data?.education ?? []).map(
        mapApiEducation
    )

    const uid = postProfileData?.data?.uid

    const initialHasAny = educations.length > 0
    const [state, setState] = useState<'empty' | 'list' | 'add' | 'edit'>(initialHasAny ? 'list' : 'empty')
    const [editUid, setEditUid] = useState<string | null>(null)
    const [form, setForm] = useState<Education>(defaultEducation)
    const [noEducation, setNoEducation] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})

    const descRef = useRef<HTMLTextAreaElement | null>(null)
    useEffect(() => {
        const el = descRef.current
        if (!el) return
        el.style.height = '39px'
        el.style.height = `${el.scrollHeight}px`
    }, [form.description])

    useEffect(() => {
        if (state === 'add' || state === 'edit') return
        setState(educations.length ? 'list' : 'empty')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [educations.length])

    const educationToApiBody = (data: Education) => ({
        ...(data.uid ? { uid: data.uid } : {}),
        degree: data.degree,
        toDate: `${data.endYear}-${data.endMonth || '01'}-01`,
        fromDate: `${data.startYear}-${data.startMonth || '01'}-01`,
        location: data.location,
        schoolName: data.institution,
        educationType: data.educationType,
        description: data.description,
        educationListId: 0,
    })

    const isFormValid =
        form.institution.trim() !== '' &&
        form.startMonth.trim() !== '' &&
        form.startYear.trim() !== '' &&
        form.endMonth.trim() !== '' &&
        form.endYear.trim() !== ''

    const handleFormSubmit = async () => {
        if (!uid) return
        try {
            if (editUid) {
                await putEducation({ uid, body: { ...educationToApiBody(form), uid: editUid } }).unwrap()
            } else {
                await postEducation({ uid, body: educationToApiBody(form) }).unwrap()
            }
            setState('list')
            setEditUid(null)
            setForm(defaultEducation)
            setFieldErrors({})
            postProfileRefetch()
        } catch (e: any) {
            const errFields: { [key: string]: string } = {}
            const errors = e?.data?.errors || []
            for (const err of errors) {
                const { field, msg } = err
                switch (field) {
                    case 'schoolName':
                        errFields.institution = msg || 'Required field'
                        break
                    case 'degree':
                        errFields.degree = msg || 'Required field'
                        break
                    case 'educationType':
                        errFields.educationType = msg || 'Required field'
                        break
                    case 'location':
                        errFields.location = msg || 'Required field'
                        break
                    case 'description':
                        errFields.description = msg || 'Required field'
                        break
                    case 'fromDate': {
                        const toDateError = errors.find((e: any) => e.field === 'toDate')
                        errFields.startYear = toDateError?.msg || msg || 'Invalid date'
                        break
                    }
                    case 'toDate':
                        errFields.endYear = msg || 'Invalid date'
                        break
                }
            }
            setFieldErrors(errFields)
        }
    }

    const handleDeleteClick = async (idx: number) => {
        if (!uid) return
        const education = educations[idx]
        if (!education?.uid) return
        try {
            await deleteEducation({ uid, educationUid: education.uid }).unwrap()
            setState('list')
            postProfileRefetch()
        } catch (e) {
            console.error('Failed to delete education', e)
        }
    }

    const handleAddClick = () => {
        setForm(defaultEducation)
        setEditUid(null)
        setState('add')
    }

    const handleEditClick = (idx: number) => {
        const edu = educations[idx]
        setForm(edu)
        setEditUid(edu.uid || null)
        setState('edit')
    }

    const handleFormChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = (
        e
    ) => {
        const { name, value } = e.currentTarget
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const handleCancel = () => {
        setState(educations.length ? 'list' : 'empty')
        setEditUid(null)
        setForm(defaultEducation)
    }

    // ---- UI ----
    return (
        <div className="EducationStep">
            {state === 'empty' && (
                <>
                    <div className="edu-empty">
                        <div className="edu-empty-icon">
                            <EducationIcon />
                        </div>
                        <div className="edu-empty-content">
                            <div className="edu-empty-title">
                                {noEducation
                                    ? t('10308', "I don't have any education")
                                    : t('10296', 'No education added yet')}
                            </div>
                            <div className="edu-empty-actions">
                                <button
                                    className="add-edu-btn"
                                    type="button"
                                    disabled={noEducation}
                                    onClick={handleAddClick}
                                >
                                    + {t('1255', 'Add education')}
                                </button>
                                {/*<div className="no-edu-toggle" onClick={() => setNoEducation((v) => !v)}>*/}
                                {/*    <div className={`toggle-switch${noEducation ? ' checked' : ''}`}>*/}
                                {/*        <div className="toggle-thumb" />*/}
                                {/*    </div>*/}
                                {/*    <span>{t('6180', 'No education')}</span>*/}
                                {/*</div>*/}
                                <ApplyV2NoFieldsSwitch
                                    className={`${baseClass}__empty--desc-switch`}
                                    checked={noEducation}
                                    onChange={setNoEducation}
                                    label={getTitle('6180', 'No education')}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row right">
                        {/*<button type="button" disabled={!noEducation} className="next-btn" onClick={onNextStep}>*/}
                        {/*    {t('3035', 'Next')}*/}
                        {/*</button>*/}
                        <ApplyV2Button
                            variant={'primary'}
                            className={`${baseClass}__next--btn`}
                            onClick={onNextStep}
                            isLoading={isPostApplied || isPostProfileLoading}
                            // isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && references.length === 0)}
                            isDisabled={isPostApplied || isPostProfileLoading}
                            type={'submit'}
                        >
                            {getTitle('3035', 'Next')}
                        </ApplyV2Button>
                    </div>
                </>
            )}

            {(state === 'add' || state === 'edit') && (
                <div className="edu-form">
                    <div className="edu-form-header">
                        <span>{state === 'edit' ? 'Edit education' : 'Add education'}</span>
                        {/*<button className="close-btn" onClick={handleCancel}>*/}
                        {/*    ×*/}
                        {/*</button>*/}
                        <Tooltip
                            hasArrow
                            // isDisabled={isLoading}
                            openDelay={500}
                            label={getTitle('416', 'Cancel')}
                            // placement={'right'}
                            padding={'8px 16px'}
                            borderRadius={'4px'}
                            backgroundColor={'#19191A'}
                            fontWeight={600}
                            fontSize={'12px'}
                            shouldWrapChildren
                        >
                            <ApplyV2Button
                                className={clsx(`${baseClass}__header--buttons-btn`, {})}
                                variant={'transparent'}
                                size={'small'}
                                onClick={handleCancel}
                                leftIcon={<i className="fa-regular fa-xmark"></i>}
                            />
                        </Tooltip>
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('6710', 'Educational Institution')}*</label>
                            <input
                                className={fieldErrors.institution ? 'has-error' : ''}
                                name="institution"
                                value={form.institution}
                                onChange={handleFormChange}
                                maxLength={255}
                                placeholder={t('6711', 'Your school, university, etc') as string}
                            />
                        </div>
                        {fieldErrors.institution && <div className="field-error">{fieldErrors.institution}</div>}
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('6724', 'Degree') as string}</label>
                            <input
                                className={fieldErrors.degree ? 'has-error' : ''}
                                name="degree"
                                value={form.degree}
                                onChange={handleFormChange}
                                maxLength={255}
                                placeholder={t('6725', 'Bachelor, Master, etc') as string}
                            />
                        </div>
                        {fieldErrors.degree && <div className="field-error">{fieldErrors.degree}</div>}
                    </div>

                    <div className=" ">
                        <div className="field">
                            <label>{t('3293', 'Field of study') as string}</label>
                            <input
                                className={fieldErrors.educationType ? 'has-error' : ''}
                                name="educationType"
                                value={form.educationType}
                                onChange={handleFormChange}
                                maxLength={255}
                                placeholder={t('8686', 'E.g. Computer Science') as string}
                            />
                        </div>
                        {fieldErrors.educationType && <div className="field-error">{fieldErrors.educationType}</div>}
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('1249', 'Location') as string}</label>
                            <input
                                className={fieldErrors.location ? 'has-error' : ''}
                                name="location"
                                value={form.location}
                                onChange={handleFormChange}
                                maxLength={512}
                                placeholder={t('1249', 'City, Country') as string}
                            />
                        </div>
                        {fieldErrors.location && <div className="field-error">{fieldErrors.location}</div>}
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('325', 'Start date') as string}*</label>
                            <div className="dob-input">
                                <ApplyV2Select
                                    name="startMonth"
                                    className="dob-select"
                                    placeholder={t('931', 'Month') as string}
                                    value={form.startMonth}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'startMonth', value: String(value ?? '') },
                                        } as any)
                                    }
                                    options={monthOptions}
                                />
                                <ApplyV2Select
                                    name="startYear"
                                    className="dob-select"
                                    placeholder={t('1322', 'Year') as string}
                                    value={form.startYear}
                                    options={startYearOptions}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'startYear', value: String(value ?? '') },
                                        } as any)
                                    }
                                    errorMessage={fieldErrors.startYear}
                                />
                            </div>
                            {fieldErrors.startYear && <div className="field-error">{fieldErrors.startYear}</div>}
                        </div>
                    </div>

                    <div className="">
                        <div className="field">
                            <label>{t('326', 'End date') as string}*</label>
                            <div className="dob-input">
                                <ApplyV2Select
                                    name="endMonth"
                                    className="dob-select"
                                    placeholder={t('931', 'Month') as string}
                                    value={form.endMonth}
                                    options={monthOptions}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'endMonth', value: String(value ?? '') },
                                        } as any)
                                    }
                                    errorMessage={fieldErrors.endMonth}
                                />
                                <ApplyV2Select
                                    name="endYear"
                                    className="dob-select"
                                    placeholder={t('1322', 'Year') as string}
                                    value={form.endYear}
                                    options={endYearOptions}
                                    onChange={(value) =>
                                        handleFormChange({
                                            currentTarget: { name: 'endYear', value: String(value ?? '') },
                                        } as any)
                                    }
                                    errorMessage={fieldErrors.endYear}
                                />
                            </div>
                            {fieldErrors.endYear && <div className="field-error">{fieldErrors.endYear}</div>}
                        </div>
                    </div>

                    <div className="">
                        <div className="field full">
                            <label>{t('606', 'Description') as string}</label>
                            <textarea
                                ref={descRef}
                                className={`auto-textarea${fieldErrors.description ? ' has-error' : ''}`}
                                name="description"
                                value={form.description}
                                onChange={handleFormChange}
                                placeholder={t('1650', 'Description') as string}
                                rows={1}
                                maxLength={5000}
                            />
                        </div>
                        {fieldErrors.description && <div className="field-error">{fieldErrors.description}</div>}
                    </div>

                    <div className="row actions">
                        <button type="button" className="cancel-btn" onClick={handleCancel}>
                            {t('416', 'Cancel') as string}
                        </button>
                        <button
                            type="button"
                            className="add-btn"
                            onClick={handleFormSubmit}
                            disabled={!isFormValid || isPostLoading || isPutLoading}
                        >
                            {state === 'edit' ? 'Save' : 'Add'}
                        </button>
                    </div>
                </div>
            )}

            {state === 'list' && (
                <div>
                    <div className="edu-list edu-card">
                        {educations.map((edu, i) => (
                            <ApplyV2Card
                                key={edu.uid || i}
                                className="edu-block"
                                title={decodeHtmlEntitiesRecursive(edu.institution)}
                                headerDescription={decodeHtmlEntitiesRecursive(
                                    [edu.degree, edu.educationType].filter(Boolean).join(' • ')
                                )}
                                localeSection={edu.location}
                                date={{
                                    startYear: edu.startYear,
                                    endYear: edu.endYear,
                                    startMonth: edu.startMonth,
                                    endMonth: edu.endMonth,
                                }}
                                onEdit={() => handleEditClick(i)}
                                onDelete={() => handleDeleteClick(i)}
                            >
                                {edu.description}
                            </ApplyV2Card>
                        ))}

                        <div className="add-edu-row">
                            {/*<button className="add-edu-btn" type="button" onClick={handleAddClick}>*/}
                            {/*    + {t('1255', 'Add education')}*/}
                            {/*</button>*/}
                            <ApplyV2Button
                                variant={'transparent'}
                                className={`${baseClass}__addBtn`}
                                onClick={handleAddClick}
                                leftIcon={
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.99967 1.33301C8.36786 1.33301 8.66634 1.63148 8.66634 1.99967V7.12611H13.9997C14.3679 7.12611 14.6663 7.42459 14.6663 7.79278C14.6663 8.16097 14.3679 8.45944 13.9997 8.45944H8.66634V13.9997C8.66634 14.3679 8.36786 14.6663 7.99967 14.6663C7.63148 14.6663 7.33301 14.3679 7.33301 13.9997V8.45944H1.99967C1.63148 8.45944 1.33301 8.16097 1.33301 7.79278C1.33301 7.42459 1.63148 7.12611 1.99967 7.12611H7.33301V1.99967C7.33301 1.63148 7.63148 1.33301 7.99967 1.33301Z"
                                            fill="#19191A"
                                        />
                                    </svg>
                                }
                            >
                                {getTitle('1255', 'Add education')}
                            </ApplyV2Button>
                        </div>
                    </div>

                    <div className="row right">
                        {/*<button type="button" className="next-btn" onClick={onNextStep}>*/}
                        {/*    {t('3035', 'Next')}*/}
                        {/*</button>*/}
                        <ApplyV2Button
                            variant={'primary'}
                            className={`${baseClass}__next--btn`}
                            onClick={onNextStep}
                            isLoading={isPostApplied || isPostProfileLoading}
                            // isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && references.length === 0)}
                            isDisabled={isPostApplied || isPostProfileLoading}
                            type={'submit'}
                        >
                            {getTitle('3035', 'Next')}
                        </ApplyV2Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default EducationStep
